import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { ErrorNote } from './ui';

/**
 * One way to add a record. A primary button in the page heading opens a form
 * under it and reads Cancel while it is open; the form closes once the record
 * is in. Where the page can take a file as well, Bulk import stands beside it
 * and the two take turns: one disclosure at a time, so at most one heading
 * button reads Cancel.
 *
 * Periods and Classes used to have an always-open box instead, and Holidays a
 * form with no button at all. The pattern lives here so the pages cannot drift
 * apart again.
 */

export type Disclosure = 'add' | 'import' | null;

export function AddToggles({
  panel,
  onChange,
  add,
  importable = false,
}: {
  panel: Disclosure;
  onChange: (panel: Disclosure) => void;
  /** The primary button's label: `Add student`. */
  add: string;
  /** Whether the page takes a CSV as well. */
  importable?: boolean;
}) {
  const toggle = (which: 'add' | 'import') => onChange(panel === which ? null : which);
  return (
    <div className="flex flex-wrap gap-2">
      {importable ? (
        <button
          type="button"
          className="btn"
          aria-expanded={panel === 'import'}
          onClick={() => toggle('import')}
        >
          {panel === 'import' ? 'Cancel' : 'Bulk import'}
        </button>
      ) : null}
      <button
        type="button"
        className="btn btn-primary"
        aria-expanded={panel === 'add'}
        onClick={() => toggle('add')}
      >
        {panel === 'add' ? 'Cancel' : add}
      </button>
    </div>
  );
}

/**
 * The sunk panel the fields sit in: the error across the top, the submit
 * across the bottom, the fields between them on the shared grid. The children
 * are the labelled fields and nothing else.
 */
export function AddForm({
  error,
  fallback,
  pending,
  submit,
  onSubmit,
  children,
}: {
  /** The mutation's error, if any. The server's reason is shown when it gave one. */
  error: unknown;
  /** `Could not add student.`: what to say when the failure was not the server's answer. */
  fallback: string;
  pending: boolean;
  /** The submit button's label; it reads `Adding…` while pending. */
  submit: string;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <form
      className="panel mb-5 grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {error ? (
        <div className="sm:col-span-2 lg:col-span-4">
          <ErrorNote message={error instanceof ApiError ? error.message : fallback} />
        </div>
      ) : null}
      {children}
      <div className="sm:col-span-2 lg:col-span-4">
        <button className="btn btn-primary" type="submit" disabled={pending}>
          {pending ? 'Adding…' : submit}
        </button>
      </div>
    </form>
  );
}
